import styles from "../../styles/common.module.css";
import { useTranslation } from "react-i18next";

export interface Step {
  /** Locale key for the step label. */
  description: string;
}

interface StepTrackerProps {
  steps: Step[];
  /** 1-based index of the step in progress. */
  currentStep: number;
}

/**
 * Progress through the capture flow.
 *
 * Follows the step indicator used on the IwC platform: a small numbered marker
 * with its label beside it, a hairline connector between steps, the brand amber
 * for the step in progress and a green tick for the ones behind it.
 *
 * Replaces a component that took seven class-name props and rendered either an
 * image or a number. It had one caller and one shape in use, so the styling now
 * lives in the stylesheet where it can be changed without threading props.
 */
const StepTracker = ({ steps, currentStep }: StepTrackerProps) => {
  const { t } = useTranslation();

  return (
    <nav className={styles.stepTracker} aria-label={t("onboardstep.progress")}>
      <ol className={styles.stepTrackerInner}>
        {steps.map((step, index) => {
          const position = index + 1;
          const done = position < currentStep;
          const active = position === currentStep;

          const stepClass = done
            ? styles.stepDone
            : active
              ? styles.stepActive
              : styles.stepPending;
          const markerClass = done
            ? styles.markerDone
            : active
              ? styles.markerActive
              : styles.markerPending;

          return (
            <li key={step.description} className={styles.stepItem}>
              <div
                className={`${styles.step} ${stepClass}`}
                aria-current={active ? "step" : undefined}
              >
                <span className={`${styles.stepMarker} ${markerClass}`}>
                  {done ? (
                    <svg
                      className={styles.markerCheck}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    position
                  )}
                </span>
                <span className={styles.stepLabel}>{t(step.description)}</span>
              </div>

              {index < steps.length - 1 && (
                <span
                  className={`${styles.stepConnector} ${done ? styles.connectorDone : ""}`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default StepTracker;
